import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Alert, Collapse, MenuItem, Select, FormControl, InputLabel,
  Radio, RadioGroup, FormControlLabel, Divider,
} from '@mui/material';
import {
  Share as ShareIcon, Edit as EditIcon, Delete as DeleteIcon,
  Check as CheckIcon, Close as CloseIcon, Add as AddIcon,
  PersonSearch as PersonSearchIcon, Send as SendIcon,
  ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import accessService from '../services/accessService';
import usersService from '../services/usersService';
import invitesService from '../services/invitesService';
import professionalsService from '../services/professionalsService';
import useSnackbar from '../hooks/useSnackbar';
import { tableContainer, tableHeadCell, tableBodyCell } from '../styles/pageStyles';
import PageLayout from '../components/ui/PageLayout';

const entityTypeLabels = {
  1: 'Profissional',
  2: 'Agendamentos',
  3: 'Agendas',
  4: 'Clientes',
  Professional: 'Profissional',
  Appointments: 'Agendamentos',
  Schedules: 'Agendas',
  Persons: 'Clientes',
};

const accessLevelLabels = {
  1: 'Visualização',
  2: 'Edição',
  Read: 'Visualização',
  ReadWrite: 'Edição',
};

const entityTypeToKey = (et) => ({
  1: 'professional', 2: 'appointments', 3: 'schedules', 4: 'persons',
  Professional: 'professional', Appointments: 'appointments', Schedules: 'schedules', Persons: 'persons',
}[et] || 'unknown');

const accessLevelToNum = (al) => {
  if (al === 'ReadWrite' || al === 2) return 2;
  if (al === 'Read' || al === 1) return 1;
  return 0;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

export default function AccessPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Grant form
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [grantPermissions, setGrantPermissions] = useState({
    professional: 1,
    appointments: 0,
    schedules: 0,
    persons: 0,
  });
  const [grantLoading, setGrantLoading] = useState(false);

  // Edit state
  const [editingKey, setEditingKey] = useState(null);
  const [editPermissions, setEditPermissions] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  // Revoke
  const [revokeConfirmKey, setRevokeConfirmKey] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await accessService.getOverview();
      setItems(result.items || []);
    } catch (err) {
      setError('Erro ao carregar acessos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProfessionals = useCallback(async () => {
    try {
      const data = await professionalsService.getAll();
      const profs = data?.professionals || [];
      const owned = profs.filter(p => !p.isShared);
      setProfessionals(owned);
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Search email
  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const user = await usersService.lookupByEmail(searchEmail.trim());
      setSearchResult({ found: true, user });
    } catch (err) {
      if (err.response?.status === 404) {
        setSearchResult({ found: false });
      } else {
        showError('Erro ao buscar usuário');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Permission change with validation
  const handleGrantPermissionChange = (key, value) => {
    setGrantPermissions(prev => {
      const updated = { ...prev, [key]: value };
      if (key === 'professional') updated.professional = Math.max(value, 1);
      if (key === 'appointments' && value === 2 && updated.schedules === 0) updated.schedules = 1;
      if (key === 'schedules' && value === 0 && updated.appointments === 2) updated.appointments = 1;
      return updated;
    });
  };

  // Grant access
  const handleGrant = async () => {
    if (!searchResult?.found || !searchResult.user || !selectedProfessionalId) return;

    const permissionsList = [];
    permissionsList.push({ entityType: 1, accessLevel: grantPermissions.professional });
    if (grantPermissions.appointments > 0) permissionsList.push({ entityType: 2, accessLevel: grantPermissions.appointments });
    if (grantPermissions.schedules > 0) permissionsList.push({ entityType: 3, accessLevel: grantPermissions.schedules });
    if (grantPermissions.persons > 0) permissionsList.push({ entityType: 4, accessLevel: grantPermissions.persons });

    setGrantLoading(true);
    try {
      await accessService.grantAccess({
        professionalId: selectedProfessionalId,
        userId: searchResult.user.id,
        permissions: permissionsList,
      });
      showSuccess(`Acesso compartilhado com ${searchResult.user.name}`);
      resetGrantForm();
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao compartilhar acesso');
    } finally {
      setGrantLoading(false);
    }
  };

  // Send invite
  const handleSendInvite = async () => {
    if (!searchEmail.trim()) return;
    try {
      await invitesService.create({
        email: searchEmail.trim(),
        context: 'professional_access',
        message: 'Convite para colaborar no Agendário',
      });
      showSuccess(`Convite enviado para ${searchEmail.trim()}`);
      setSearchResult(null);
      setSearchEmail('');
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao enviar convite');
    }
  };

  const resetGrantForm = () => {
    setShowGrantForm(false);
    setSelectedProfessionalId('');
    setSearchEmail('');
    setSearchResult(null);
    setGrantPermissions({ professional: 1, appointments: 0, schedules: 0, persons: 0 });
  };

  // Edit
  const handleStartEdit = (item) => {
    const key = `${item.professionalId}-${item.grantedToUserId}`;
    setEditingKey(key);
    const perms = { professional: 0, appointments: 0, schedules: 0, persons: 0 };
    item.permissions.forEach(p => {
      perms[entityTypeToKey(p.entityType)] = accessLevelToNum(p.accessLevel);
    });
    if (!perms.professional) perms.professional = 1;
    setEditPermissions(perms);
  };

  const handleEditPermissionChange = (key, value) => {
    setEditPermissions(prev => {
      const updated = { ...prev, [key]: value };
      if (key === 'professional') updated.professional = Math.max(value, 1);
      if (key === 'appointments' && value === 2 && updated.schedules === 0) updated.schedules = 1;
      if (key === 'schedules' && value === 0 && updated.appointments === 2) updated.appointments = 1;
      return updated;
    });
  };

  const handleSaveEdit = async (item) => {
    const permissionsList = [];
    if (editPermissions.professional > 0) permissionsList.push({ entityType: 1, accessLevel: editPermissions.professional });
    if (editPermissions.appointments > 0) permissionsList.push({ entityType: 2, accessLevel: editPermissions.appointments });
    if (editPermissions.schedules > 0) permissionsList.push({ entityType: 3, accessLevel: editPermissions.schedules });
    if (editPermissions.persons > 0) permissionsList.push({ entityType: 4, accessLevel: editPermissions.persons });

    setEditLoading(true);
    try {
      await accessService.updateAccess(item.professionalId, item.grantedToUserId, { permissions: permissionsList });
      showSuccess('Permissões atualizadas');
      setEditingKey(null);
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao atualizar permissões');
    } finally {
      setEditLoading(false);
    }
  };

  // Revoke
  const handleRevoke = async (item) => {
    const targetUserId = item.direction === 'given' ? item.grantedToUserId : item.grantedToUserId;
    setRevokeLoading(true);
    try {
      await accessService.revokeAccess(item.professionalId, targetUserId);
      showSuccess('Acesso revogado');
      setRevokeConfirmKey(null);
      loadData();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao revogar acesso');
    } finally {
      setRevokeLoading(false);
    }
  };

  // Permission radio row renderer
  const renderPermissionRadios = (perms, onChange, disabled = false) => (
    <Table size="small" sx={{ mt: 1, mb: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Entidade</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Nenhum</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Visualização</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Edição</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[
          { key: 'professional', label: 'Profissional', noNone: true },
          { key: 'appointments', label: 'Agendamentos' },
          { key: 'schedules', label: 'Agendas' },
          { key: 'persons', label: 'Clientes' },
        ].map(({ key, label, noNone }) => (
          <TableRow key={key}>
            <TableCell sx={{ py: 0.5 }}>{label}</TableCell>
            <TableCell align="center" sx={{ py: 0.5 }}>
              {noNone ? '—' : (
                <Radio size="small" checked={perms[key] === 0} onChange={() => onChange(key, 0)} disabled={disabled} />
              )}
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5 }}>
              <Radio size="small" checked={perms[key] === 1} onChange={() => onChange(key, 1)} disabled={disabled} />
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5 }}>
              <Radio size="small" checked={perms[key] === 2} onChange={() => onChange(key, 2)} disabled={disabled} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageLayout
      themeKey="access"
      actionButton={
        <Button
          variant="contained"
          startIcon={showGrantForm ? <CloseIcon /> : <AddIcon />}
          onClick={() => {
            if (!showGrantForm) loadProfessionals();
            setShowGrantForm(!showGrantForm);
            if (showGrantForm) resetGrantForm();
          }}
        >
          {showGrantForm ? 'Cancelar' : 'Compartilhar Acesso'}
        </Button>
      }
    >
      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Grant Form — inline, not modal */}
      <Collapse in={showGrantForm}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderColor: 'primary.main', borderWidth: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            <ShareIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
            Novo Compartilhamento
          </Typography>

          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {/* Profissional */}
            <FormControl fullWidth size="small">
              <InputLabel>Profissional</InputLabel>
              <Select
                value={selectedProfessionalId}
                label="Profissional"
                onChange={(e) => setSelectedProfessionalId(e.target.value)}
              >
                {professionals.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Email */}
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                label="Email do usuário"
                size="small"
                fullWidth
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="nome@email.com"
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={searchLoading || !searchEmail.trim()}
                startIcon={searchLoading ? <CircularProgress size={16} /> : <PersonSearchIcon />}
                sx={{ minWidth: 120 }}
              >
                Buscar
              </Button>
            </Box>

            {/* Search result */}
            {searchResult && searchResult.found && (
              <Alert severity="success">
                Usuário encontrado: <strong>{searchResult.user.name}</strong> ({searchResult.user.email})
              </Alert>
            )}
            {searchResult && !searchResult.found && (
              <Alert
                severity="info"
                action={
                  <Button size="small" startIcon={<SendIcon />} onClick={handleSendInvite}>
                    Enviar convite
                  </Button>
                }
              >
                Nenhum usuário encontrado. Envie um convite para criar conta.
              </Alert>
            )}

            {/* Permissions */}
            {searchResult?.found && selectedProfessionalId && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Permissões</Typography>
                {renderPermissionRadios(grantPermissions, handleGrantPermissionChange)}
                <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                  Profissional (Visualização) é obrigatório. Agendamentos (Edição) exige Agendas (Visualização).
                </Alert>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button variant="outlined" onClick={resetGrantForm}>Cancelar</Button>
                  <Button
                    variant="contained"
                    onClick={handleGrant}
                    disabled={grantLoading}
                    startIcon={grantLoading ? <CircularProgress size={16} /> : <ShareIcon />}
                  >
                    Compartilhar
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Collapse>

      {/* Access List Table */}
      {items.length === 0 && !loading ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <ShareIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Nenhum compartilhamento encontrado. Use o botão acima para compartilhar um profissional.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={tableContainer}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={tableHeadCell}>Tipo</TableCell>
                <TableCell sx={tableHeadCell}>Profissional</TableCell>
                <TableCell sx={tableHeadCell}>Compartilhado com</TableCell>
                <TableCell sx={tableHeadCell}>Permissões</TableCell>
                <TableCell sx={tableHeadCell}>Criado em</TableCell>
                <TableCell sx={tableHeadCell}>Editado em</TableCell>
                <TableCell sx={tableHeadCell} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const key = `${item.professionalId}-${item.grantedToUserId}`;
                const isEditing = editingKey === key;
                const isRevokeConfirm = revokeConfirmKey === key;

                return (
                  <TableRow key={key} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    {/* Direction */}
                    <TableCell sx={tableBodyCell}>
                      {item.direction === 'given' ? (
                        <Chip
                          icon={<ArrowUpIcon sx={{ fontSize: 14 }} />}
                          label="Enviado"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<ArrowDownIcon sx={{ fontSize: 14 }} />}
                          label="Recebido"
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </TableCell>

                    {/* Professional */}
                    <TableCell sx={tableBodyCell}>
                      <Typography variant="body2" fontWeight={500}>{item.professionalName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.direction === 'given' ? '' : `Dono: ${item.ownerName}`}
                      </Typography>
                    </TableCell>

                    {/* Shared with */}
                    <TableCell sx={tableBodyCell}>
                      <Typography variant="body2">
                        {item.direction === 'given' ? item.grantedToName : item.ownerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.direction === 'given' ? item.grantedToEmail : item.ownerEmail}
                      </Typography>
                    </TableCell>

                    {/* Permissions */}
                    <TableCell sx={tableBodyCell}>
                      {isEditing ? (
                        renderPermissionRadios(editPermissions, handleEditPermissionChange)
                      ) : (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {item.permissions.map((p, idx) => (
                            <Chip
                              key={idx}
                              label={`${entityTypeLabels[p.entityType]}: ${accessLevelLabels[p.accessLevel]}`}
                              size="small"
                              variant="outlined"
                              color={p.accessLevel === 2 || p.accessLevel === 'ReadWrite' ? 'success' : 'default'}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>

                    {/* Created at */}
                    <TableCell sx={tableBodyCell}>
                      <Typography variant="caption">{formatDateTime(item.createdAt)}</Typography>
                    </TableCell>

                    {/* Updated at */}
                    <TableCell sx={tableBodyCell}>
                      <Typography variant="caption">{formatDateTime(item.updatedAt)}</Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={tableBodyCell} align="right">
                      {isEditing ? (
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Salvar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSaveEdit(item)}
                              disabled={editLoading}
                            >
                              {editLoading ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton size="small" onClick={() => setEditingKey(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : isRevokeConfirm ? (
                        <Box display="flex" gap={0.5} justifyContent="flex-end" alignItems="center">
                          <Typography variant="caption" color="error">Revogar?</Typography>
                          <Tooltip title="Confirmar revogação">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRevoke(item)}
                              disabled={revokeLoading}
                            >
                              {revokeLoading ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton size="small" onClick={() => setRevokeConfirmKey(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          {item.direction === 'given' && (
                            <Tooltip title="Editar permissões">
                              <IconButton size="small" color="primary" onClick={() => handleStartEdit(item)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Revogar acesso">
                            <IconButton size="small" color="error" onClick={() => setRevokeConfirmKey(key)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={hideSnackbar}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </PageLayout>
  );
}
